import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventsStrip = () => {
    const [banners, setBanners] = useState([]);

    useEffect(() => {
        axios
            .get(`${API}/events/banners`)
            .then((res) => setBanners(res.data || []))
            .catch(() => setBanners([]));
    }, []);

    if (banners.length === 0) return null;

    return (
        <section id="events" className="py-20 md:py-28 px-6 md:px-10 bg-[#262055]" data-testid="events-section">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                >
                    <p className="font-serif-brand italic text-xl text-[#F0841B] mb-3">Nights at the Avenue</p>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white" data-testid="events-headline">
                        What's On
                    </h2>
                </motion.div>

                <div className="mt-12 grid md:grid-cols-2 gap-8" data-testid="events-grid">
                    {banners.map((b, i) => (
                        <motion.article
                            key={b.id}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="group relative rounded-[2rem] overflow-hidden min-h-[320px] flex items-end shadow-2xl"
                            data-testid={`event-card-${b.id}`}
                        >
                            {(b.content_type || "").startsWith("video") ? (
                                <video
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    data-testid={`event-video-${b.id}`}
                                >
                                    <source src={`${API}/files/${b.image_path}`} type={b.content_type} />
                                </video>
                            ) : (
                                <img
                                    src={`${API}/files/${b.image_path}`}
                                    alt={b.title}
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    loading="lazy"
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/90 via-[#1C1C1E]/25 to-transparent" />
                            <div className="relative z-10 p-8 md:p-10 w-full">
                                {b.event_date && (
                                    <span className="inline-flex items-center gap-2 rounded-full bg-[#F0841B] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 mb-4" data-testid={`event-date-${b.id}`}>
                                        <CalendarDays size={13} /> {b.event_date}
                                    </span>
                                )}
                                <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight" data-testid={`event-title-${b.id}`}>{b.title}</h3>
                                {b.subtitle && <p className="mt-2 text-sm md:text-base text-white/80 max-w-md" data-testid={`event-subtitle-${b.id}`}>{b.subtitle}</p>}
                                {b.booking_link && (
                                    <a
                                        href={b.booking_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-[#262055] text-sm font-semibold px-6 py-3 hover:bg-[#FFF7C5] transition-colors duration-200"
                                        data-testid={`event-booking-link-${b.id}`}
                                    >
                                        Book a Spot <ArrowUpRight size={16} />
                                    </a>
                                )}
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
